import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { eachDayOfInterval, endOfMonth, format, startOfMonth } from 'date-fns';
import { SOCCER_CLUBS } from '../constants/soccerData';
import { useLanguage } from '../context/LanguageContext';
import '../pages/DaysForecast.css';

const localizedDateNames = {
  ka: {
    months: ['იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი', 'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი'],
    weekdays: ['ორშ', 'სამ', 'ოთხ', 'ხუთ', 'პარ', 'შაბ', 'კვი']
  },
  en: {
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  },
  ru: {
    months: ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'],
    weekdays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
  }
};

const ForecastFilters = ({ tickets = [], onFilteredTicketsChange, titleKey = 'forecast.history' }) => {
  const { language, t } = useLanguage();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedClub, setSelectedClub] = useState('');
  const [dateMode, setDateMode] = useState('single');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [selectedMonthFilter, setSelectedMonthFilter] = useState(null);
  const [monthNavDate, setMonthNavDate] = useState(new Date());
  const [calendarTarget, setCalendarTarget] = useState('single');
  const [showCalendar, setShowCalendar] = useState(false);
  const [showClubDropdown, setShowClubDropdown] = useState(false);
  const [clubSearchTerm, setClubSearchTerm] = useState('');

  const dateNames = localizedDateNames[language] || localizedDateNames.ka;
  const clubs = useMemo(() => ['ყველა კლუბი', ...Object.values(SOCCER_CLUBS).flat()], []);
  const filteredClubs = clubs.filter(club => club.toLowerCase().includes(clubSearchTerm.toLowerCase()));

  useEffect(() => {
    let filtered = tickets;

    if (dateMode === 'single' && selectedDate) {
      const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
      filtered = filtered.filter(ticket => ticket.date?.split('T')[0] === selectedDateStr);
    } else if (dateMode === 'range' && dateFrom) {
      const fromStr = format(dateFrom, 'yyyy-MM-dd');
      const toStr = dateTo ? format(dateTo, 'yyyy-MM-dd') : fromStr;
      filtered = filtered.filter(ticket => {
        const date = ticket.date?.split('T')[0];
        return date && date >= fromStr && date <= toStr;
      });
    } else if (dateMode === 'month' && selectedMonthFilter) {
      const monthStr = format(selectedMonthFilter, 'yyyy-MM');
      filtered = filtered.filter(ticket => ticket.date?.split('T')[0]?.startsWith(monthStr));
    }

    if (selectedClub && selectedClub !== 'ყველა კლუბი') {
      filtered = filtered.filter(ticket => ticket.homeTeam === selectedClub || ticket.awayTeam === selectedClub);
    }

    onFilteredTicketsChange(filtered);
  }, [dateFrom, dateMode, dateTo, onFilteredTicketsChange, selectedClub, selectedDate, selectedMonthFilter, tickets]);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setShowCalendar(false);
        setShowClubDropdown(false);
      }
    };
    const handleClickOutside = (event) => {
      if (showClubDropdown && !event.target.closest('.club-filter-wrapper')) setShowClubDropdown(false);
    };

    document.addEventListener('keydown', handleEscapeKey);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showClubDropdown]);

  const formatDate = (date) => language === 'ka'
    ? `${date.getDate()} ${dateNames.months[date.getMonth()]} ${date.getFullYear()}`
    : `${dateNames.months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;

  const getDaysInMonth = (date) => eachDayOfInterval({ start: startOfMonth(date), end: endOfMonth(date) });
  const hasTicketsOnDate = (date) => tickets.some(ticket => ticket.date?.split('T')[0] === format(date, 'yyyy-MM-dd'));
  const isInRange = (date) => {
    if (dateMode !== 'range' || !dateFrom || !dateTo) return false;
    const value = format(date, 'yyyy-MM-dd');
    return value > format(dateFrom, 'yyyy-MM-dd') && value < format(dateTo, 'yyyy-MM-dd');
  };

  const changeDateMode = (mode) => {
    setDateMode(mode);
    setSelectedDate(null);
    setDateFrom(null);
    setDateTo(null);
    setSelectedMonthFilter(null);
    setCalendarTarget('single');
    setShowCalendar(false);
  };

  const clearDateFilter = () => {
    setSelectedDate(null);
    setDateFrom(null);
    setDateTo(null);
    setSelectedMonthFilter(null);
  };

  const selectDate = (date) => {
    if (calendarTarget === 'from') {
      setDateFrom(date);
      if (dateTo && date > dateTo) setDateTo(null);
      setCalendarTarget('to');
      return;
    }
    if (calendarTarget === 'to') {
      if (dateFrom && date < dateFrom) {
        setDateFrom(date);
        setDateTo(dateFrom);
      } else {
        setDateTo(date);
      }
      setShowCalendar(false);
      return;
    }
    setSelectedDate(date);
    setShowCalendar(false);
  };

  return (
    <section className="history-section weekly-history-filters">
      <h3 className="history-title">📊 {t(titleKey)}</h3>
      <div className="filters-container-modern">
        <div className="filter-card date-filter-card combined-filter-card">
          <div className="date-mode-selector">
            <button className={`date-mode-btn ${dateMode === 'single' ? 'active' : ''}`} onClick={() => changeDateMode('single')}>📅 {t('filters.single')}</button>
            <button className={`date-mode-btn ${dateMode === 'range' ? 'active' : ''}`} onClick={() => changeDateMode('range')}>📆 {t('filters.range')}</button>
            <button className={`date-mode-btn ${dateMode === 'month' ? 'active' : ''}`} onClick={() => changeDateMode('month')}>🗓️ {t('filters.month')}</button>
          </div>

          <div className="filter-controls-row">
            {dateMode === 'single' && (
              <div className="date-filter">
                <button className="date-selector-modern" onClick={() => { setCalendarTarget('single'); setShowCalendar(!showCalendar); }}>
                  <span>{selectedDate ? formatDate(selectedDate) : t('filters.chooseDate')}</span>
                  <span className="dropdown-arrow">▼</span>
                </button>
                {selectedDate && <button className="clear-button" onClick={clearDateFilter} title={t('common.clear')}>✕</button>}
              </div>
            )}

            {dateMode === 'range' && (
              <div className="date-range-container">
                <div className="date-range-row">
                  <div className="date-range-field">
                    <span className="date-range-label">{t('filters.start')}</span>
                    <button className="date-range-btn" onClick={() => { setCalendarTarget('from'); setShowCalendar(true); }}>{dateFrom ? formatDate(dateFrom) : '-- / -- / ----'}</button>
                  </div>
                  <span className="date-range-arrow">→</span>
                  <div className="date-range-field">
                    <span className="date-range-label">{t('filters.end')}</span>
                    <button className="date-range-btn" onClick={() => { setCalendarTarget('to'); setShowCalendar(true); }}>{dateTo ? formatDate(dateTo) : '-- / -- / ----'}</button>
                  </div>
                  {(dateFrom || dateTo) && <button className="clear-button" onClick={clearDateFilter} title={t('common.clear')}>✕</button>}
                </div>
              </div>
            )}

            {dateMode === 'month' && (
              <div className="month-filter-container">
                <div className="month-nav">
                  <button className="nav-button-modern" onClick={() => setMonthNavDate(new Date(monthNavDate.getFullYear(), monthNavDate.getMonth() - 1))}>‹</button>
                  <span className="month-nav-label">{dateNames.months[monthNavDate.getMonth()]} {monthNavDate.getFullYear()}</span>
                  <button className="nav-button-modern" onClick={() => setMonthNavDate(new Date(monthNavDate.getFullYear(), monthNavDate.getMonth() + 1))}>›</button>
                </div>
                <div className="month-action-row">
                  <button className={`select-month-btn ${selectedMonthFilter ? 'selected' : ''}`} onClick={() => setSelectedMonthFilter(new Date(monthNavDate.getFullYear(), monthNavDate.getMonth(), 1))}>
                    {selectedMonthFilter ? '✓ შერჩეულია' : 'ამ თვის არჩევა'}
                  </button>
                  {selectedMonthFilter && <button className="clear-button" onClick={clearDateFilter} title={t('common.clear')}>✕</button>}
                </div>
              </div>
            )}

            <div className="club-filter-wrapper">
              <div className="club-dropdown-container">
                <button className="club-dropdown-button" onClick={() => setShowClubDropdown(!showClubDropdown)}>
                  <span>{selectedClub && selectedClub !== 'ყველა კლუბი' ? selectedClub : t('filters.allClubs')}</span>
                  <span className="dropdown-arrow">▼</span>
                </button>
                {selectedClub && selectedClub !== 'ყველა კლუბი' && <button className="clear-button" onClick={() => setSelectedClub('')} title={t('common.clear')}>✕</button>}
                {showClubDropdown && (
                  <motion.div className="club-dropdown-menu" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
                    <div className="club-search-wrapper">
                      <input className="club-search-input" type="text" placeholder={`🔎 ${t('filters.searchClub')}`} value={clubSearchTerm} onChange={(event) => setClubSearchTerm(event.target.value)} autoFocus />
                    </div>
                    <div className="club-options-list">
                      {filteredClubs.length > 0 ? filteredClubs.map(club => (
                        <button key={club} className={`club-option ${selectedClub === club ? 'selected' : ''}`} onClick={() => { setSelectedClub(club === 'ყველა კლუბი' ? '' : club); setShowClubDropdown(false); setClubSearchTerm(''); }}>
                          {club === 'ყველა კლუბი' ? `⚽ ${t('filters.allClubs')}` : `👉 ${club}`}
                        </button>
                      )) : <div className="no-results">{t('filters.clubNotFound')}</div>}
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>

          {showCalendar && (
            <div className="calendar-modal" onClick={(event) => { if (event.target === event.currentTarget) setShowCalendar(false); }}>
              <motion.div className="calendar-content-modern" onClick={(event) => event.stopPropagation()} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
                <div className="calendar-sheet-label">
                  <span aria-hidden="true">📅</span>
                  <span>{dateMode === 'range' ? (calendarTarget === 'from' ? t('filters.start') : t('filters.end')) : t('filters.chooseDate')}</span>
                </div>
                <div className="calendar-header-modern">
                  <button className="nav-button-modern" onClick={() => setMonthNavDate(new Date(monthNavDate.getFullYear(), monthNavDate.getMonth() - 1))}>‹</button>
                  <span className="month-year-modern">{dateNames.months[monthNavDate.getMonth()]} {monthNavDate.getFullYear()}</span>
                  <button className="nav-button-modern" onClick={() => setMonthNavDate(new Date(monthNavDate.getFullYear(), monthNavDate.getMonth() + 1))}>›</button>
                  <button className="close-calendar-modern" onClick={() => setShowCalendar(false)} aria-label="დახურვა">✕</button>
                </div>
                <div className="weekdays-row">{dateNames.weekdays.map(day => <div key={day} className="weekday-label">{day}</div>)}</div>
                <div className="calendar-grid-modern">
                  {(() => {
                    const firstDay = getDaysInMonth(monthNavDate)[0].getDay();
                    const offset = firstDay === 0 ? 6 : firstDay - 1;
                    return Array.from({ length: offset }, (_, index) => <div key={`spacer-${index}`} className="calendar-day-spacer" />);
                  })()}
                  {getDaysInMonth(monthNavDate).map(date => (
                    <button key={date.toISOString()} className={`calendar-day-modern ${isInRange(date) ? 'in-range' : ''} ${hasTicketsOnDate(date) ? 'has-tickets' : ''}`} onClick={() => selectDate(date)} disabled={!hasTicketsOnDate(date)}>
                      {date.getDate()}
                    </button>
                  ))}
                </div>
              </motion.div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ForecastFilters;
